import { Spinner, TableCell, TableHead, TableRow } from '@/components/ui'
import { cn } from '@/lib/utils'
import { format as formatLocalized } from '@/utils/date-fns/format'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, isSameDay, parseISO } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import { ChevronRightIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { formatNumber } from '../driver-grade/metrics'
import {
  filterDrivers,
  useDriverWorkFilter,
  useDriverWorkFilterForm,
} from './driver-work-filter'
import { DriverWorkLayout } from './driver-work-header'
import { formatHours } from './hours'
import { useRowSort } from './row-sort'
import {
  DRIVER_OVERTIME_WEEKS_QUERY,
  DRIVER_OVERTIME_YEAR_QUERY,
} from './queries'
import { ScrollTable } from './scroll-table'

type YearResult = ResultOf<
  typeof DRIVER_OVERTIME_YEAR_QUERY
>['driverOvertimeYear']
type DriverRow = YearResult['rows'][number]
type WeekRow = ResultOf<
  typeof DRIVER_OVERTIME_WEEKS_QUERY
>['driverOvertimeWeeks'][number]

interface OvertimeHours {
  workingHours: number
  dayOvertimeHours?: number
  weekOvertimeHours?: number
  overtimeHours: number
}

const COLUMNS = 7
const LEVEL_PADDING = ['pl-5', 'pl-11', 'pl-17']

const hours = (value: number | undefined) => (value ? formatHours(value) : '–')

const OvertimeCells: React.FC<{ row: OvertimeHours; count?: number }> = ({
  row,
  count,
}) => (
  <>
    <TableCell end className="tabular-nums">
      {hours(row.workingHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {row.dayOvertimeHours === undefined ? '' : hours(row.dayOvertimeHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {row.weekOvertimeHours === undefined ? '' : hours(row.weekOvertimeHours)}
    </TableCell>
    <TableCell
      end
      className={cn(
        'font-semibold tabular-nums',
        row.overtimeHours ? 'text-amber-700' : 'text-stone-400'
      )}
    >
      {hours(row.overtimeHours)}
    </TableCell>
    <TableCell end className="tabular-nums">
      {count ?? ''}
    </TableCell>
  </>
)

const ToggleLabel: React.FC<{
  open: boolean
  level: number
  children: React.ReactNode
}> = ({ open, level, children }) => (
  <TableCell className={cn('pr-4', LEVEL_PADDING[level])}>
    <span className="inline-flex items-center gap-2">
      <ChevronRightIcon
        className={cn(
          'h-4 w-4 shrink-0 text-stone-400 transition-transform',
          open && 'rotate-90'
        )}
      />
      {children}
    </span>
  </TableCell>
)

const span = (start: Date, end: Date) =>
  isSameDay(start, end)
    ? `${formatLocalized(start, 'HH:mm')} – ${formatLocalized(end, 'HH:mm')}`
    : `${formatLocalized(start, 'HH:mm')} – ${formatLocalized(
        end,
        'EEE HH:mm'
      )}`

const WorkDayRows: React.FC<{ week: WeekRow }> = ({ week }) => (
  <>
    {week.days.map(day => {
      const start = parseISO(day.start)
      return (
        <TableRow key={day.start} className="bg-stone-50 text-sm">
          <TableCell className={cn(LEVEL_PADDING[2], 'capitalize')}>
            {formatLocalized(start, 'EEE d. MMM')}
          </TableCell>
          <TableCell className="tabular-nums">
            {span(start, parseISO(day.end))}
          </TableCell>
          <OvertimeCells row={day} />
        </TableRow>
      )
    })}
  </>
)

const WeekRows: React.FC<{
  week: WeekRow
  open: boolean
  onToggle(): void
}> = ({ week, open, onToggle }) => {
  const translations = useTranslate({ week: 'common.week' })
  const monday = parseISO(week.weekStart)

  return (
    <>
      <TableRow
        className="cursor-pointer bg-stone-50/60 hover:bg-stone-100"
        onClick={onToggle}
      >
        <ToggleLabel open={open} level={1}>
          <span>
            {translations.week} {week.isoWeek}
            <span className="ml-2 text-xs text-stone-400">
              {formatLocalized(monday, 'd. MMM')} –{' '}
              {formatLocalized(addDays(monday, 6), 'd. MMM')}
            </span>
          </span>
        </ToggleLabel>
        <TableCell />
        <OvertimeCells
          row={week}
          count={week.days.filter(day => day.overtimeHours).length}
        />
      </TableRow>
      {open && <WorkDayRows week={week} />}
    </>
  )
}

const DriverWeekRows: React.FC<{
  user: string
  year: number
  openWeeks: Set<string>
  onToggle(key: string): void
}> = ({ user, year, openWeeks, onToggle }) => {
  const { ownedVehiclesOnly } = useDriverWorkFilter().filter
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_OVERTIME_WEEKS_QUERY, {
    variables: { user, year, ownedVehiclesOnly },
  })
  const data = current ?? previousData

  if (loading && !data)
    return (
      <TableRow className="bg-stone-50">
        <TableCell colSpan={COLUMNS} className={LEVEL_PADDING[1]}>
          <Spinner className="size-4" />
        </TableCell>
      </TableRow>
    )

  return (
    <>
      {(data?.driverOvertimeWeeks ?? [])
        .filter(week => week.days.length)
        .map(week => {
          const key = `${user}:${week.weekStart}`
          return (
            <WeekRows
              key={key}
              week={week}
              open={openWeeks.has(key)}
              onToggle={() => onToggle(key)}
            />
          )
        })}
    </>
  )
}

const OvertimeTable: React.FC<{
  rows: DriverRow[]
  year: number
  loading: boolean
}> = ({ rows, year, loading }) => {
  const translations = useTranslate({
    level: 'driver-work.driver-week-workday',
    startEnd: 'driver-work.start-end',
    working: 'driver-work.total',
    dayOvertime: 'driver-work.day-overtime',
    weekOvertime: 'driver-work.week-overtime',
    overtime: 'driver-work.overtime',
    days: 'driver-work.days-with-overtime',
    noData: 'driver-work.no-data',
  })
  const [open, setOpen] = useState<Set<string>>(new Set())
  const { sorted, headProps } = useRowSort(rows, {
    name: row => row.user.fullName,
    working: row => row.workingHours,
    dayOvertime: row => row.dayOvertimeHours,
    weekOvertime: row => row.weekOvertimeHours,
    overtime: row => row.overtimeHours,
    days: row => row.daysWithOvertime,
  })

  const toggle = (key: string) =>
    setOpen(previous => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <ScrollTable
      header={
        <TableRow>
          <TableHead {...headProps('name')}>{translations.level}</TableHead>
          <TableHead>{translations.startEnd}</TableHead>
          <TableHead end {...headProps('working')}>
            {translations.working}
          </TableHead>
          <TableHead end {...headProps('dayOvertime')}>
            {translations.dayOvertime}
          </TableHead>
          <TableHead end {...headProps('weekOvertime')}>
            {translations.weekOvertime}
          </TableHead>
          <TableHead end {...headProps('overtime')}>
            {translations.overtime}
          </TableHead>
          <TableHead end {...headProps('days')}>
            {translations.days}
          </TableHead>
        </TableRow>
      }
    >
      {loading && (
        <TableRow>
          <TableCell colSpan={COLUMNS}>
            <Spinner />
          </TableCell>
        </TableRow>
      )}
      {!loading && !rows.length && (
        <TableRow>
          <TableCell colSpan={COLUMNS} className="text-center text-stone-500">
            {translations.noData}
          </TableCell>
        </TableRow>
      )}
      {sorted.map(driver => {
        const id = driver.user.id
        return (
          <DriverRows
            key={id}
            driver={driver}
            year={year}
            open={open.has(id)}
            openWeeks={open}
            onToggle={toggle}
          />
        )
      })}
    </ScrollTable>
  )
}

const DriverRows: React.FC<{
  driver: DriverRow
  year: number
  open: boolean
  openWeeks: Set<string>
  onToggle(key: string): void
}> = ({ driver, year, open, openWeeks, onToggle }) => (
  <>
    <TableRow
      className="cursor-pointer hover:bg-stone-50"
      onClick={() => onToggle(driver.user.id)}
    >
      <ToggleLabel open={open} level={0}>
        <span className="font-semibold text-stone-900">
          {driver.user.fullName}
        </span>
      </ToggleLabel>
      <TableCell />
      <OvertimeCells row={driver} count={driver.daysWithOvertime} />
    </TableRow>
    {open && (
      <DriverWeekRows
        user={driver.user.id}
        year={year}
        openWeeks={openWeeks}
        onToggle={onToggle}
      />
    )}
  </>
)

const OvertimeRules: React.FC<{ limits: YearResult | undefined }> = ({
  limits,
}) => {
  const translations = useTranslate({
    rules: 'driver-work.rules',
    workDay: ['driver-work.rule-work-day', { hours: '' }],
    day: ['driver-work.rule-day', { hours: '' }],
    week: ['driver-work.rule-week', { hours: '' }],
    total: 'driver-work.rule-total',
    year: 'driver-work.rule-year',
  })
  if (!limits) return null
  const number = (value: number) => formatNumber(value, value % 1 ? 1 : 0)

  return (
    <div className="mb-4 overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="border-b border-stone-200 px-5 pt-4 pb-3 text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
        {translations.rules}
      </div>
      <ul className="grid gap-x-6 gap-y-2 px-5 py-4 text-sm text-stone-700 md:grid-cols-2">
        <li>
          {translations.workDay({ hours: number(limits.dailyRestHours) })}
        </li>
        <li>{translations.day({ hours: number(limits.dayLimitHours) })}</li>
        <li>{translations.week({ hours: number(limits.weekLimitHours) })}</li>
        <li>{translations.total}</li>
        <li>{translations.year}</li>
      </ul>
    </div>
  )
}

export const DriverOvertimePage: React.FC = () => {
  const form = useDriverWorkFilterForm()
  const { year, drivers, ownedVehiclesOnly } = form.filter
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_OVERTIME_YEAR_QUERY, {
    variables: { year, ownedVehiclesOnly },
  })
  const data = current ?? previousData
  const rows = useMemo(
    () => filterDrivers(data?.driverOvertimeYear.rows ?? [], drivers),
    [data, drivers]
  )

  return (
    <DriverWorkLayout form={form} views={false}>
      <OvertimeRules limits={data?.driverOvertimeYear} />
      <OvertimeTable
        key={year}
        rows={rows}
        year={year}
        loading={loading && !data}
      />
    </DriverWorkLayout>
  )
}
